/**
 * exam-center — 「出考卷中心」:依課程選範圍出對應考卷(boss 2026-06-14
 * 「應該讓使用者選課程去產生」)。
 *
 * 統一上層:選科目(5 科,顯示課本版本)→ 選範圍(該科章節/單元/課,從課程索引列出)
 * → 依「科目 + 範圍」路由到最適產生方式(`exam-center-route.ts`),產生考卷並**標示
 * 產生方式**(✅ 程式產生 / 📚 題庫組卷)→ 線上作答(復用既有考卷排版/批改)+ 列印 A4
 * +「重抽」。錯題回流錯題本。
 *
 * 與既有並存:複習中心的「英語聽力卷」「數學考卷」兩張卡不動(本中心是依課程選範圍
 * 的統一入口,可與既有並存)。
 *
 * 復用(不重造):
 *   - 數學程式卷:`generateMathExam` + `runMathExam` + `generateMathExamHtml`。
 *   - 英語聽力卷:`generateListeningExam` + `runListeningExam` + `generateListeningExamHtml`。
 *   - 題庫卷:`loadBankFor` + `sampleGroupedPractice` + `buildBankPaperPrintHtml`(bank-paper)。
 *
 * 視覺 = 學術風格設計系統(pageShell / academicCard / academicButton),專業介面
 * (無音效 / mascot / 彩光)。學習/遊戲分離:純學習側,絕不碰 commanderXp / 戰鬥數值。
 */

import { pageShell, academicCard, academicButton } from '../ui/components';
import { goBack } from '../nav/back-nav';
import type { Subject } from '../../shared/types';
import {
  listExamUnits,
  listReviewUnits,
  planExamForUnit,
  textbookFor,
  type ExamPlan,
  type ExamRange,
  type ExamUnit,
  type GradeSemester,
} from './exam-center-route';
import { loadGradeSemester } from '../grade-semester';

import { generateMathExam } from './math-exam-gen';
import type { MathExam, MathExamItem, MathSectionKind } from './math-exam-gen';
import { runMathExam } from './math-exam';
import type { MathExamScore, MathExamWrong } from './math-exam';
import { generateMathExamHtml, generateListeningExamHtml, generateStatsTableExamHtml, generateTimeExamHtml, generateMulDivExamHtml, generateCircleExamHtml, generateDecimalExamHtml } from '../printable/pdf-gen';

import { generateStatsTableExam } from './stats-table-gen';
import type { StatsTableExam } from './stats-table-gen';
import { runStatsTableExam } from './stats-table-exam';
import type { StatsExamScore, StatsExamWrong } from './stats-table-exam';

import { generateTimeExam } from './time-exam-gen';
import type { TimeExam } from './time-exam-gen';
import { runTimeExam } from './time-exam';
import type { TimeExamScore, TimeExamWrong } from './time-exam';

import { generateMulDivExam } from './mul-div-gen';
import type { MulDivExam } from './mul-div-gen';
import { runMulDivExam } from './mul-div-exam';
import type { MulDivExamScore, MulDivExamWrong } from './mul-div-exam';

import { generateCircleExam } from './circle-gen';
import type { CircleExam } from './circle-gen';
import { runCircleExam } from './circle-exam';
import type { CircleExamScore, CircleExamWrong } from './circle-exam';

import { generateDecimalExam } from './decimal-gen';
import type { DecimalExam } from './decimal-gen';
import { runDecimalExam } from './decimal-exam';
import type { DecimalExamScore, DecimalExamWrong } from './decimal-exam';

import { generateListeningExam } from './listening-exam-gen';
import { runListeningExam } from './listening-exam';
import type { ListeningExam } from './listening-exam-types';

import { loadBankFor, sampleGroupedPractice, isAnswerable, type EntryQuestion } from '../entry/question-source';
import { buildBankPaperPrintHtml } from './bank-paper';

import { loadProgress, saveProgress } from '../../shared/progress';
import { recordMistake, deriveQuestionId } from '../../shared/mistake-log';
import { feedMistakesToWiki } from './wiki-bridge';

// ───────────────────────────── 科目 ─────────────────────────────

const SUBJECTS: { id: Subject; label: string; emoji: string }[] = [
  { id: 'math', label: '數學', emoji: '⚡' },
  { id: 'chinese', label: '國語', emoji: '🔮' },
  { id: 'english', label: '英語', emoji: '🤖' },
  { id: 'nature', label: '自然', emoji: '🌀' },
  { id: 'social', label: '社會', emoji: '🏰' },
];

/**
 * 讀玩家「當前學年」—— 委派給 `grade-semester` 單一真相(沒設 ⇒ 三下 3.2)。
 * 出考卷中心只列當前學年的單元,其餘學年收進「複習舊單元」摺疊。
 */
function currentGradeSemester(): GradeSemester {
  return loadGradeSemester();
}

/** 學年小數 → 短標籤(UI 摺疊 / 副標用,如「三下」)。設定頁用長標(grade-semester)。 */
function gradeLabel(g: GradeSemester): string {
  return g === 3.1 ? '三上' : g === 4.1 ? '四上' : g === 4.2 ? '四下' : '三下';
}

/** 把單元(章節)轉成既有 `ExamRange`(產生 / 動作頁沿用,label = 單元名)。 */
function unitToRange(unit: ExamUnit): ExamRange {
  return {
    kpId: unit.kpId,
    label: unit.title,
    chapter: unit.title,
    chapterNumber: unit.chapterNumber,
  };
}

/** 題庫卷一次抽幾題。 */
const BANK_PAPER_COUNT = 12;
/** 題庫卷每答對一題回流計分(對齊 math-exam-launch 不發 XP,只回流錯題)。 */
const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥'];

// ───────────────────────────── 重抽 seed ─────────────────────────────

let reshuffleCounter = 0;
/** 重抽 seed:時間戳 + 單調遞增,保證連抽永遠換新卷;同 seed 仍可重現。 */
function freshSeed(): number {
  reshuffleCounter = (reshuffleCounter + 1) % 1_000_000;
  return (Math.floor(Date.now()) + reshuffleCounter * 7919) % 1_000_000;
}

// ───────────────────────────── 入口 ─────────────────────────────

/**
 * Render 出考卷中心 into `container`。
 * @param container 掛載點。
 * @param onDone    離開回呼。
 */
export function runExamCenter(container: HTMLElement, onDone: () => void): void {
  renderSubjectPick(container, onDone);
}

// 1. 選科目 ──────────────────────────────────────────────────────────

function renderSubjectPick(container: HTMLElement, onDone: () => void): void {
  container.innerHTML = '';
  const shell = pageShell(container, {
    title: '出考卷中心',
    subtitle: '選科目 → 選課程範圍 → 產生對應考卷。可線上作答或列印 A4,錯題自動回流錯題本。',
    onBack: onDone,
  });

  const root = document.createElement('div');
  root.className = 'exam-center ec-subjects';
  root.setAttribute('data-testid', 'exam-center');
  root.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
  shell.body.appendChild(root);

  const step = document.createElement('p');
  step.className = 'ec-step';
  step.style.cssText = 'margin:0 0 4px;font-size:14px;color:var(--ef-sub);';
  step.textContent = '選科目:';
  root.appendChild(step);

  for (const s of SUBJECTS) {
    const book = textbookFor(s.id);
    const card = academicCard({
      icon: s.emoji,
      label: s.label,
      sub: book ? `${book}版 · 三下` : '三下',
      onClick: () => {
        shell.unmount();
        renderRangePick(container, s.id, onDone);
      },
    });
    card.setAttribute('data-subject', s.id);
    card.classList.add('ec-subject-card');
    root.appendChild(card);
  }
}

// 2. 選範圍 ──────────────────────────────────────────────────────────

function renderRangePick(container: HTMLElement, subject: Subject, onDone: () => void): void {
  container.innerHTML = '';
  const subjMeta = SUBJECTS.find((s) => s.id === subject)!;
  const book = textbookFor(subject);
  const grade = currentGradeSemester();
  const back = (): void => renderSubjectPick(container, onDone);
  const shell = pageShell(container, {
    title: `${subjMeta.emoji} ${subjMeta.label}考卷`,
    subtitle: `${book ? `${book}版 · ` : ''}${gradeLabel(grade)} · 點一個單元,直接出那單元一整張卷。`,
    onBack: back,
  });

  const root = document.createElement('div');
  root.className = 'exam-center ec-ranges';
  root.setAttribute('data-testid', 'ec-ranges');
  root.setAttribute('data-grade', String(grade));
  root.style.cssText = 'display:flex;flex-direction:column;gap:10px;';
  shell.body.appendChild(root);

  const reRender = (): void => renderRangePick(container, subject, onDone);

  /** 把一顆單元卡掛上 root(或指定容器)。 */
  const addUnitCard = (parent: HTMLElement, unit: ExamUnit): void => {
    const plan = planExamForUnit(subject, unit);
    const range = unitToRange(unit);
    const card = academicCard({
      // 圖標一律統一(boss 2026-06-14「一下打勾一下書本看不懂」):家長 / 小孩不需知道
      // 卷是「程式產生」還是「題庫組卷」,只要「點單元→出一張卷」。實作差異留 data-method。
      icon: '📝',
      label: `第 ${unit.chapterNumber} 章 ${unit.title}`,
      sub: '出一張練習卷',
      onClick: () => {
        shell.unmount();
        void generateAndShow(container, plan, range, onDone, reRender);
      },
    });
    card.setAttribute('data-kp', unit.kpId);
    card.setAttribute('data-method', plan.method);
    card.setAttribute('data-unit', String(unit.chapterNumber));
    card.classList.add('ec-unit-card');
    parent.appendChild(card);
  };

  // ── 當前學年單元(預設畫面只有這些 + 離開)──────────────────────────
  const units = listExamUnits(subject, grade);
  for (const unit of units) addUnitCard(root, unit);

  if (units.length === 0) {
    const empty = document.createElement('p');
    empty.style.cssText = 'font-size:14px;color:var(--ef-sub);margin:8px 0;';
    empty.textContent = '這個科目目前還沒有當前學年的單元。';
    root.appendChild(empty);
  }

  // ── 複習舊單元(三上 / 四上)摺疊,預設收合 ──────────────────────────
  const reviewUnits = listReviewUnits(subject, grade);
  if (reviewUnits.length > 0) {
    const details = document.createElement('details');
    details.className = 'ec-review-units';
    details.setAttribute('data-testid', 'ec-review-units');
    details.style.cssText =
      'margin-top:14px;border-top:1px solid var(--ef-line);padding-top:10px;';

    const summary = document.createElement('summary');
    summary.style.cssText =
      'cursor:pointer;font-family:var(--ef-serif);font-weight:700;font-size:14px;' +
      'color:var(--ef-sub);list-style:none;user-select:none;padding:4px 0;';
    summary.textContent = `▾ 複習舊單元(${reviewUnits.length})`;
    details.appendChild(summary);

    const body = document.createElement('div');
    body.style.cssText = 'display:flex;flex-direction:column;gap:8px;margin-top:8px;';
    let lastGrade: GradeSemester | null = null;
    for (const unit of reviewUnits) {
      if (unit.gradeSemester !== lastGrade) {
        lastGrade = unit.gradeSemester;
        const gh = document.createElement('div');
        gh.style.cssText =
          'font-size:13px;font-weight:700;color:var(--ef-sub);margin:6px 0 2px;';
        gh.textContent = `【${gradeLabel(unit.gradeSemester)}】複習`;
        body.appendChild(gh);
      }
      addUnitCard(body, unit);
    }
    details.appendChild(body);
    root.appendChild(details);
  }
  // 返回(換科目)一律走左上角統一「‹ 返回」/ 左緣滑動(pageShell onBack=back),
  // 不再另放底部「← 換科目」鈕,避免一頁兩個返回鈕(boss 2026-06-14 要求統一)。
}

// 3. 產生 + 動作頁 ────────────────────────────────────────────────────

/** 產生器產出的卷(union),用於動作頁分流。 */
type GeneratedExam =
  | { method: 'program'; kind: 'math'; exam: MathExam }
  | { method: 'program'; kind: 'stats-table'; exam: StatsTableExam }
  | { method: 'program'; kind: 'time'; exam: TimeExam }
  | { method: 'program'; kind: 'mul-div'; exam: MulDivExam }
  | { method: 'program'; kind: 'circle'; exam: CircleExam }
  | { method: 'program'; kind: 'decimal'; exam: DecimalExam }
  | { method: 'program'; kind: 'listening'; exam: ListeningExam }
  | { method: 'bank'; title: string; questions: EntryQuestion[] };

/** 依 plan 產生一份卷(重抽用 fresh seed)。bank 路徑非同步。 */
async function buildExam(plan: ExamPlan, range: ExamRange): Promise<GeneratedExam | null> {
  const seed = freshSeed();
  if (plan.method === 'program' && plan.programKind === 'math') {
    const kps: MathSectionKind[] | undefined =
      plan.mathKps && plan.mathKps.length > 0 ? plan.mathKps : undefined;
    const exam = generateMathExam({
      seed,
      kps,
      title: `${range.label} · 數學考卷`,
    });
    return { method: 'program', kind: 'math', exam };
  }
  if (plan.method === 'program' && plan.programKind === 'stats-table') {
    const exam = generateStatsTableExam({
      seed,
      title: `${range.label} · 統計表考卷`,
    });
    return { method: 'program', kind: 'stats-table', exam };
  }
  if (plan.method === 'program' && plan.programKind === 'time') {
    const exam = generateTimeExam({
      seed,
      title: `${range.label} · 時間考卷`,
    });
    return { method: 'program', kind: 'time', exam };
  }
  if (plan.method === 'program' && plan.programKind === 'mul-div') {
    const exam = generateMulDivExam({
      seed,
      title: `${range.label} · 乘法與除法考卷`,
    });
    return { method: 'program', kind: 'mul-div', exam };
  }
  if (plan.method === 'program' && plan.programKind === 'circle') {
    const exam = generateCircleExam({
      seed,
      title: `${range.label} · 圓考卷`,
    });
    return { method: 'program', kind: 'circle', exam };
  }
  if (plan.method === 'program' && plan.programKind === 'decimal') {
    const exam = generateDecimalExam({
      seed,
      title: `${range.label} · 小數考卷`,
    });
    return { method: 'program', kind: 'decimal', exam };
  }
  if (plan.method === 'program' && plan.programKind === 'listening') {
    const exam = generateListeningExam({
      seed,
      title: `${range.label} · 英語聽力卷`,
    });
    return { method: 'program', kind: 'listening', exam };
  }
  // bank:撈題庫抽題。
  const bankKpId = plan.bankKpId ?? plan.kpId;
  try {
    const bank = await loadBankFor(bankKpId);
    if (bank) {
      const questions = sampleGroupedPractice(bank, BANK_PAPER_COUNT, seed).filter(isAnswerable);
      if (questions.length > 0) {
        return { method: 'bank', title: `${range.label} · ${subjectLabel(plan.subject)}練習卷`, questions };
      }
    }
  } catch {
    /* 載入失敗 → 回 null,UI 端顯示友善訊息 */
  }
  return null;
}

async function generateAndShow(
  container: HTMLElement,
  plan: ExamPlan,
  range: ExamRange,
  onDone: () => void,
  back: () => void,
): Promise<void> {
  container.innerHTML = '';
  const shell = pageShell(container, { title: range.label, subtitle: '正在產生考卷…', onBack: back });
  const loading = document.createElement('p');
  loading.className = 'ec-step';
  loading.style.cssText = 'font-size:14px;color:var(--ef-sub);';
  loading.textContent = '正在產生考卷…';
  shell.body.appendChild(loading);

  const generated = await buildExam(plan, range);
  shell.unmount();

  if (!generated) {
    renderEmpty(container, range, back);
    return;
  }
  renderActions(container, plan, range, generated, onDone, back);
}

function renderEmpty(container: HTMLElement, range: ExamRange, back: () => void): void {
  container.innerHTML = '';
  const shell = pageShell(container, {
    title: range.label,
    subtitle: '這個範圍暫時抽不到題目,換一個範圍試試。',
    onBack: back,
  });
  const btn = academicButton({
    label: '← 換一個範圍',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      back();
    },
  });
  shell.body.appendChild(btn);
}

function renderActions(
  container: HTMLElement,
  plan: ExamPlan,
  range: ExamRange,
  generated: GeneratedExam,
  onDone: () => void,
  back: () => void,
): void {
  container.innerHTML = '';
  const title =
    generated.method === 'bank'
      ? generated.title
      : generated.exam.title;
  const shell = pageShell(container, {
    title,
    subtitle: `${plan.methodLabel} · 選擇線上作答(即時批改 + 錯題回流)或列印 A4 紙卷。`,
    onBack: back,
  });

  const root = document.createElement('div');
  root.className = 'exam-center ec-actions';
  root.setAttribute('data-testid', 'ec-actions');
  root.setAttribute('data-method', plan.method);
  root.style.cssText = 'display:flex;flex-direction:column;gap:14px;';
  shell.body.appendChild(root);

  // 產生方式標籤(明顯標示可信度)。
  const badge = document.createElement('div');
  badge.className = 'ec-method-badge';
  badge.setAttribute('data-testid', 'ec-method-badge');
  badge.style.cssText =
    'align-self:flex-start;font-size:13px;font-weight:700;padding:6px 12px;border-radius:999px;' +
    (plan.method === 'program'
      ? 'background:#e6f4ec;color:#2f6b4f;border:1px solid #bfe0cd;'
      : 'background:#f3eee2;color:#7a6a3f;border:1px solid #e0d6bd;');
  badge.textContent = plan.methodLabel;
  root.appendChild(badge);

  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;flex-wrap:wrap;gap:12px;';
  root.appendChild(btnRow);

  // 線上作答。
  const onlineBtn = academicButton({
    label: '線上作答',
    variant: 'primary',
    onClick: () => {
      shell.unmount();
      runOnline(container, plan, range, generated, onDone, back);
    },
  });
  onlineBtn.setAttribute('data-testid', 'ec-action-online');
  btnRow.appendChild(onlineBtn);

  // 列印 A4。
  const printBtn = academicButton({
    label: '🖨️ 列印 A4',
    variant: 'secondary',
    onClick: () => openPrint(generated),
  });
  printBtn.setAttribute('data-testid', 'ec-action-print');
  btnRow.appendChild(printBtn);

  // 重抽。
  const reshuffleBtn = academicButton({
    label: '🎲 重抽一份',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      void generateAndShow(container, plan, range, onDone, back);
    },
  });
  reshuffleBtn.setAttribute('data-testid', 'ec-action-reshuffle');
  btnRow.appendChild(reshuffleBtn);

  // 換範圍。
  const backBtn = academicButton({
    label: '← 換範圍',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      back();
    },
  });
  backBtn.setAttribute('data-testid', 'ec-action-back');
  btnRow.appendChild(backBtn);
}

// ───────────────────────────── 線上作答(分流復用既有 runner)─────────────────────────────

function runOnline(
  container: HTMLElement,
  plan: ExamPlan,
  range: ExamRange,
  generated: GeneratedExam,
  onDone: () => void,
  back: () => void,
): void {
  const reActions = (): void => renderActions(container, plan, range, generated, onDone, back);
  if (generated.method === 'program' && generated.kind === 'math') {
    runMathExam(
      container,
      generated.exam,
      reActions,
      (_score: MathExamScore, wrong: MathExamWrong[]) => flowBackMathWrong(wrong),
    );
    return;
  }
  if (generated.method === 'program' && generated.kind === 'stats-table') {
    runStatsTableExam(
      container,
      generated.exam,
      reActions,
      (_score: StatsExamScore, wrong: StatsExamWrong[]) => flowBackStatsWrong(plan, wrong),
    );
    return;
  }
  if (generated.method === 'program' && generated.kind === 'time') {
    runTimeExam(
      container,
      generated.exam,
      reActions,
      (_score: TimeExamScore, wrong: TimeExamWrong[]) => flowBackTimeWrong(plan, wrong),
    );
    return;
  }
  if (generated.method === 'program' && generated.kind === 'mul-div') {
    runMulDivExam(
      container,
      generated.exam,
      reActions,
      (_score: MulDivExamScore, wrong: MulDivExamWrong[]) => flowBackMulDivWrong(plan, wrong),
    );
    return;
  }
  if (generated.method === 'program' && generated.kind === 'circle') {
    runCircleExam(
      container,
      generated.exam,
      reActions,
      (_score: CircleExamScore, wrong: CircleExamWrong[]) => flowBackCircleWrong(plan, wrong),
    );
    return;
  }
  if (generated.method === 'program' && generated.kind === 'decimal') {
    runDecimalExam(
      container,
      generated.exam,
      reActions,
      (_score: DecimalExamScore, wrong: DecimalExamWrong[]) => flowBackDecimalWrong(plan, wrong),
    );
    return;
  }
  if (generated.method === 'program' && generated.kind === 'listening') {
    runListeningExam(container, generated.exam, reActions);
    return;
  }
  // bank:用本中心的考卷視圖 + 三鈕批改(錯題回流)。
  renderBankSheet(container, plan, range, generated.questions, onDone, back);
}

// ───────────────────────────── 列印 ─────────────────────────────

function openPrint(generated: GeneratedExam): void {
  let html: string;
  if (generated.method === 'program' && generated.kind === 'math') {
    html = generateMathExamHtml(generated.exam);
  } else if (generated.method === 'program' && generated.kind === 'stats-table') {
    html = generateStatsTableExamHtml(generated.exam);
  } else if (generated.method === 'program' && generated.kind === 'time') {
    html = generateTimeExamHtml(generated.exam);
  } else if (generated.method === 'program' && generated.kind === 'mul-div') {
    html = generateMulDivExamHtml(generated.exam);
  } else if (generated.method === 'program' && generated.kind === 'circle') {
    html = generateCircleExamHtml(generated.exam);
  } else if (generated.method === 'program' && generated.kind === 'decimal') {
    html = generateDecimalExamHtml(generated.exam);
  } else if (generated.method === 'program' && generated.kind === 'listening') {
    html = generateListeningExamHtml(generated.exam);
  } else {
    html = buildBankPaperPrintHtml(generated.title, generated.questions);
  }
  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
}

// ───────────────────────────── 數學錯題回流 ─────────────────────────────

/** 各數學大題 → 錯題本 KP(對齊 math-exam-launch SECTION_KP,名實相符)。 */
const MATH_SECTION_KP: Record<MathSectionKind, string> = {
  compute: 'math.div.remainder',
  mulvert: 'math.mul.3x1',
  arrow: 'math.div.mul-relation',
  inverse: 'math.div.mul-relation',
  remword: 'math.div.remainder',
  twostep: 'math.word.two-step',
  word: 'math.div.mul-relation',
};

function flowBackMathWrong(wrong: MathExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return;
  const feeds = [];
  for (const w of wrong) {
    const kp = MATH_SECTION_KP[w.sectionKind];
    const answer = (w.item as MathExamItem).answer;
    recordMistake(progress, {
      questionId: deriveQuestionId(w.item.prompt),
      kp,
      subject: 'math',
      prompt: w.item.prompt,
      correctAnswer: answer,
      userAnswer: null,
    });
    feeds.push({ subject: 'math' as const, kp, prompt: w.item.prompt, correctAnswer: answer });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

// ───────────────────────────── 統計表錯題回流 ─────────────────────────────

/** 統計表卷錯題回流:回 plan.kpId(= math.stats.table 統計表單元),純學習側不發 XP。 */
function flowBackStatsWrong(plan: ExamPlan, wrong: StatsExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return;
  const feeds = [];
  for (const w of wrong) {
    recordMistake(progress, {
      questionId: deriveQuestionId(w.question.prompt),
      kp: plan.kpId,
      subject: 'math',
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
      userAnswer: null,
    });
    feeds.push({
      subject: 'math' as const,
      kp: plan.kpId,
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
    });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

// ───────────────────────────── 時間卷錯題回流 ─────────────────────────────

/** 時間卷錯題回流:回 plan.kpId(= math.time.clock / math.time.duration),純學習側不發 XP。 */
function flowBackTimeWrong(plan: ExamPlan, wrong: TimeExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return;
  const feeds = [];
  for (const w of wrong) {
    recordMistake(progress, {
      questionId: deriveQuestionId(w.question.prompt),
      kp: plan.kpId,
      subject: 'math',
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
      userAnswer: null,
    });
    feeds.push({
      subject: 'math' as const,
      kp: plan.kpId,
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
    });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

// ───────────────────────────── 乘除卷錯題回流 ─────────────────────────────

/** 乘除卷錯題回流:回 plan.kpId(= math.div.mul-relation 乘法與除法單元),純學習側不發 XP。 */
function flowBackMulDivWrong(plan: ExamPlan, wrong: MulDivExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return;
  const feeds = [];
  for (const w of wrong) {
    recordMistake(progress, {
      questionId: deriveQuestionId(w.question.prompt),
      kp: plan.kpId,
      subject: 'math',
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
      userAnswer: null,
    });
    feeds.push({
      subject: 'math' as const,
      kp: plan.kpId,
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
    });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

// ───────────────────────────── 圓卷錯題回流 ─────────────────────────────

/** 圓卷錯題回流:回 plan.kpId(= math.shape.circle 圓單元),純學習側不發 XP。圓規操作題(printOnly)
 *  線上不批改 → 不會進 wrong,故不回流。 */
function flowBackCircleWrong(plan: ExamPlan, wrong: CircleExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return;
  const feeds = [];
  for (const w of wrong) {
    recordMistake(progress, {
      questionId: deriveQuestionId(w.question.prompt),
      kp: plan.kpId,
      subject: 'math',
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
      userAnswer: null,
    });
    feeds.push({
      subject: 'math' as const,
      kp: plan.kpId,
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
    });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

// ───────────────────────────── 小數卷錯題回流 ─────────────────────────────

/** 小數卷錯題回流:回 plan.kpId(= math.decimal.basic / math.decimal.addsub 小數單元),純學習側不發 XP。 */
function flowBackDecimalWrong(plan: ExamPlan, wrong: DecimalExamWrong[]): void {
  if (wrong.length === 0) return;
  const progress = loadProgress();
  if (!progress) return;
  const feeds = [];
  for (const w of wrong) {
    recordMistake(progress, {
      questionId: deriveQuestionId(w.question.prompt),
      kp: plan.kpId,
      subject: 'math',
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
      userAnswer: null,
    });
    feeds.push({
      subject: 'math' as const,
      kp: plan.kpId,
      prompt: w.question.prompt,
      correctAnswer: w.question.answer,
    });
  }
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
}

// ───────────────────────────── 題庫卷:考卷視圖 + 三鈕批改 ─────────────────────────────

function renderBankSheet(
  container: HTMLElement,
  plan: ExamPlan,
  range: ExamRange,
  questions: EntryQuestion[],
  onDone: () => void,
  back: () => void,
): void {
  container.innerHTML = '';
  const title = `${range.label} · ${subjectLabel(plan.subject)}練習卷`;
  const shell = pageShell(container, {
    title,
    subtitle: '寫完按「對答案」,家長三鈕批改(對 / 粗心 / 不會),錯題回流錯題本。',
    onBack: back,
    confirmOnBack: '離開後這張卷的作答不會保留,確定?',
  });

  const root = document.createElement('div');
  root.className = 'exam-center ec-sheet';
  root.setAttribute('data-testid', 'ec-bank-sheet');
  root.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
  shell.body.appendChild(root);

  questions.forEach((q, i) => {
    const item = document.createElement('div');
    item.className = 'ec-q';
    item.style.cssText =
      'border:1px solid var(--ef-line);border-radius:var(--ef-radius);padding:12px 14px;background:var(--ef-paper-bright);';
    const prompt = document.createElement('div');
    prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);white-space:pre-wrap;';
    prompt.textContent = `${i + 1}. ${q.prompt}`;
    item.appendChild(prompt);
    if (q.kind === 'mcq') {
      const opts = document.createElement('div');
      opts.style.cssText = 'font-size:14px;color:var(--ef-ink);margin-top:4px;';
      opts.textContent = q.options.map((o, j) => `${CIRCLED[j] ?? `(${j + 1})`} ${o}`).join('　');
      item.appendChild(opts);
    }
    root.appendChild(item);
  });

  const toolbar = document.createElement('div');
  toolbar.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;margin-top:6px;';
  const gradeBtn = academicButton({
    label: '✅ 寫完了,對答案',
    variant: 'primary',
    onClick: () => {
      shell.unmount(); // 卸掉本卷的 confirmOnBack 返回處理器,讓對答案頁掛自己的。
      renderBankGrade(container, plan, range, questions, onDone, back);
    },
  });
  gradeBtn.setAttribute('data-testid', 'ec-grade-btn');
  const backBtn = academicButton({
    label: '← 返回',
    variant: 'secondary',
    // 走 goBack() → 跟 ‹ / 左緣滑動同一路徑(會先跳 confirmOnBack 確認再返回)。
    onClick: () => { goBack(); },
  });
  toolbar.append(gradeBtn, backBtn);
  root.appendChild(toolbar);
}

type Mark = 'correct' | 'careless' | 'wrong';

function renderBankGrade(
  container: HTMLElement,
  plan: ExamPlan,
  range: ExamRange,
  questions: EntryQuestion[],
  onDone: () => void,
  back: () => void,
): void {
  container.innerHTML = '';
  const shell = pageShell(container, {
    title: `${range.label} · 對答案`,
    subtitle: '逐題看正解,幫孩子標記:對 / 粗心(算對寫錯)/ 不會(觀念不懂)。沒寫的可不標。',
    onBack: back,
  });

  const root = document.createElement('div');
  root.className = 'exam-center ec-grade';
  root.setAttribute('data-testid', 'ec-grade-screen');
  root.style.cssText = 'display:flex;flex-direction:column;gap:12px;';
  shell.body.appendChild(root);

  const marks: (Mark | null)[] = questions.map(() => null);

  questions.forEach((q, idx) => {
    const card = document.createElement('div');
    card.style.cssText =
      'border:1px solid var(--ef-line);border-radius:var(--ef-radius);padding:12px 14px;background:var(--ef-paper);';
    const prompt = document.createElement('div');
    prompt.style.cssText = 'font-size:15px;color:var(--ef-ink);white-space:pre-wrap;';
    prompt.textContent = `${idx + 1}. ${q.prompt}`;
    card.appendChild(prompt);

    const correctText =
      q.kind === 'mcq' ? String(q.options[q.correctAnswer] ?? q.correctAnswer) : String(q.correctAnswer);
    const ans = document.createElement('div');
    ans.style.cssText =
      'font-size:14px;color:#b3261e;border-left:3px solid #b3261e;padding-left:8px;margin:8px 0;white-space:pre-wrap;';
    ans.textContent = q.explanation ? `正解:${correctText}\n解析:${q.explanation}` : `正解:${correctText}`;
    card.appendChild(ans);

    const row = document.createElement('div');
    row.style.cssText = 'display:flex;gap:10px;flex-wrap:wrap;';
    const opts: { mark: Mark; label: string; variant: 'primary' | 'secondary' | 'danger' }[] = [
      { mark: 'correct', label: '對', variant: 'primary' },
      { mark: 'careless', label: '粗心', variant: 'secondary' },
      { mark: 'wrong', label: '不會', variant: 'danger' },
    ];
    const buttons: HTMLButtonElement[] = [];
    for (const opt of opts) {
      const b = academicButton({ label: opt.label, variant: opt.variant, onClick: () => {} });
      b.dataset.mark = opt.mark;
      b.addEventListener('click', () => {
        marks[idx] = opt.mark;
        buttons.forEach((bb) => {
          const on = bb === b;
          bb.style.outline = on ? '2px solid var(--ef-ink)' : '';
          bb.style.outlineOffset = on ? '2px' : '';
        });
      });
      buttons.push(b);
      row.appendChild(b);
    }
    card.appendChild(row);
    root.appendChild(card);
  });

  const finish = academicButton({
    label: '完成批改',
    variant: 'primary',
    onClick: () => {
      shell.unmount(); // 卸掉對答案頁返回處理器,讓回饋頁掛自己的。
      const correctCount = applyBankGrades(plan, questions, marks);
      renderBankFeedback(container, range, correctCount, marks, onDone, back);
    },
  });
  finish.setAttribute('data-testid', 'ec-grade-finish');
  root.appendChild(finish);
}

/** 批改入帳:錯 / 粗心 → 錯題本 + wiki 回流。回答對題數。純學習側,不發 commanderXp。 */
function applyBankGrades(
  plan: ExamPlan,
  questions: EntryQuestion[],
  marks: (Mark | null)[],
): number {
  const progress = loadProgress();
  let correctCount = 0;
  if (!progress) {
    return marks.filter((m) => m === 'correct').length;
  }
  if (!Array.isArray(progress.mistakes)) progress.mistakes = [];
  const feeds: { subject: Subject; kp: string; prompt: string; correctAnswer: string }[] = [];
  questions.forEach((q, i) => {
    const mark = marks[i];
    if (mark == null) return;
    if (mark === 'correct') correctCount += 1;
    if (mark === 'wrong' || mark === 'careless') {
      const correctText =
        q.kind === 'mcq' ? String(q.options[q.correctAnswer] ?? q.correctAnswer) : String(q.correctAnswer);
      const qid = deriveQuestionId(q.prompt);
      recordMistake(progress, {
        questionId: qid,
        kp: plan.kpId,
        subject: plan.subject,
        prompt: q.prompt,
        correctAnswer: correctText,
        userAnswer: null,
        explanation: q.explanation || undefined,
      });
      if (mark === 'careless') {
        const entry = progress.mistakes.find((m) => m.questionId === qid);
        if (entry) entry.errorType = 'careless';
      }
      feeds.push({ subject: plan.subject, kp: plan.kpId, prompt: q.prompt, correctAnswer: correctText });
    }
  });
  saveProgress(progress);
  feedMistakesToWiki(progress, feeds);
  return correctCount;
}

function renderBankFeedback(
  container: HTMLElement,
  range: ExamRange,
  correctCount: number,
  marks: (Mark | null)[],
  onDone: () => void,
  back: () => void,
): void {
  container.innerHTML = '';
  const graded = marks.filter((m) => m != null).length;
  const shell = pageShell(container, {
    title: '批改完成',
    subtitle: `${range.label} · 這次對 ${correctCount} / ${graded} 題,錯題已回流錯題本。`,
    onBack: back,
  });
  const root = document.createElement('div');
  root.setAttribute('data-testid', 'ec-feedback');
  root.style.cssText = 'display:flex;gap:12px;flex-wrap:wrap;';
  shell.body.appendChild(root);

  const againBtn = academicButton({
    label: '🎲 再出一份',
    variant: 'primary',
    onClick: () => {
      shell.unmount();
      back();
    },
  });
  const doneBtn = academicButton({
    label: '完成',
    variant: 'secondary',
    onClick: () => {
      shell.unmount();
      onDone();
    },
  });
  root.append(againBtn, doneBtn);
}

// ───────────────────────────── helpers ─────────────────────────────

const SUBJECT_LABEL: Record<Subject, string> = {
  math: '數學',
  chinese: '國語',
  english: '英語',
  nature: '自然',
  social: '社會',
};

function subjectLabel(subject: Subject): string {
  return SUBJECT_LABEL[subject];
}
